import { bus } from '../core/event-bus.js';
import { playerManager } from '../core/player-manager.js';
import { getAllPresets, getCategories } from '../presets/registry.js';
import { updateUrlParams, getShareableUrl } from '../automation/url-params.js';

export function initTagInput() {
  const urlInput = document.getElementById('tag-url-input');
  const loadBtn = document.getElementById('tag-load-btn');
  const shareBtn = document.getElementById('tag-share-btn');
  const presetSelect = document.getElementById('preset-select');
  const rawXmlToggle = document.getElementById('raw-xml-toggle');
  const rawXmlContainer = document.getElementById('raw-xml-container');
  const rawXmlInput = document.getElementById('raw-xml-input');
  const rawXmlLoadBtn = document.getElementById('raw-xml-load-btn');

  _populatePresets(presetSelect);

  loadBtn.addEventListener('click', () => {
    _loadCurrentTag(urlInput, rawXmlToggle, rawXmlInput);
  });

  urlInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      _loadCurrentTag(urlInput, rawXmlToggle, rawXmlInput);
    }
  });

  rawXmlLoadBtn.addEventListener('click', () => {
    _loadRawXml(rawXmlInput);
  });

  rawXmlInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      _loadRawXml(rawXmlInput);
    }
  });

  rawXmlInput.addEventListener('paste', (e) => {
    const text = e.clipboardData?.getData('text');
    if (text) {
      e.preventDefault();
      rawXmlInput.value = prettifyXml(text);
    }
  });

  presetSelect.addEventListener('change', () => {
    const presets = getAllPresets();
    const preset = presets.find(p => p.name === presetSelect.value);
    if (preset) {
      urlInput.value = preset.url;
      rawXmlToggle.checked = false;
      rawXmlContainer.classList.add('hidden');
      updateUrlParams({ tag: preset.url, preset: preset.name, xml: null });
      bus.emit('layout:top-panels-content-changed');
    }
  });

  rawXmlToggle.addEventListener('change', () => {
    rawXmlContainer.classList.toggle('hidden', !rawXmlToggle.checked);
    bus.emit('layout:top-panels-content-changed');
  });

  // Share button -- copies shareable URL
  shareBtn?.addEventListener('click', () => {
    const url = getShareableUrl();
    navigator.clipboard.writeText(url).then(() => {
      const original = shareBtn.textContent;
      shareBtn.textContent = 'Copied!';
      setTimeout(() => { shareBtn.textContent = original; }, 2000);
    }).catch(() => {
      prompt('Copy this link:', url);
    });
  });

  bus.on('app:autostart', (tag) => {
    urlInput.value = tag;
    _loadCurrentTag(urlInput, rawXmlToggle, rawXmlInput);
  });
}

function _loadRawXml(rawXmlInput) {
  const xml = rawXmlInput.value.trim();
  if (!xml) return;
  updateUrlParams({ xml, tag: null, preset: null });
  playerManager.loadRawXml(xml, {
    sourceType: 'xml',
    sourceValue: '(raw XML)',
  });
}

function _loadCurrentTag(urlInput, rawXmlToggle, rawXmlInput) {
  if (rawXmlToggle.checked) {
    _loadRawXml(rawXmlInput);
  } else {
    const url = urlInput.value.trim();
    if (!url) return;
    const matchingPreset = _findPresetByUrl(url);
    updateUrlParams({
      tag: url,
      preset: matchingPreset?.name || null,
      xml: null,
    });
    playerManager.loadAds(url, {
      sourceType: matchingPreset ? 'preset' : 'tag',
      sourceValue: matchingPreset?.name || url,
    });
  }
}

function _findPresetByUrl(url) {
  return getAllPresets().find(p => p.url === url) || null;
}

function _populatePresets(select) {
  const categories = getCategories();
  const presets = getAllPresets();

  for (const cat of categories) {
    const group = document.createElement('optgroup');
    group.label = cat;
    const items = presets.filter(p => p.category === cat);
    for (const item of items) {
      const option = document.createElement('option');
      option.value = item.name;
      option.textContent = item.label;
      option.title = item.description;
      group.appendChild(option);
    }
    select.appendChild(group);
  }
}

/**
 * Pretty-print an XML string with 2-space indentation.
 * Returns the original string unchanged if parsing fails.
 */
export function prettifyXml(xmlString) {
  const trimmed = xmlString.trim();
  if (!trimmed) return xmlString;

  try {
    const doc = new DOMParser().parseFromString(trimmed, 'text/xml');
    if (doc.querySelector('parsererror')) return xmlString;

    const lines = [];
    _serializeNode(doc.documentElement, 0, lines);
    const body = lines.join('\n');

    const xmlDecl = trimmed.match(/^<\?xml[^?]*\?>/);
    return xmlDecl ? xmlDecl[0] + '\n' + body : body;
  } catch {
    return xmlString;
  }
}

function _serializeNode(node, depth, lines) {
  const indent = '  '.repeat(depth);

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent.trim();
    if (text) lines.push(indent + text);
    return;
  }

  if (node.nodeType === Node.COMMENT_NODE) {
    lines.push(indent + '<!--' + node.textContent + '-->');
    return;
  }

  if (node.nodeType === Node.CDATA_SECTION_NODE) {
    lines.push(indent + '<![CDATA[' + node.textContent + ']]>');
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  let open = '<' + node.tagName;
  for (const attr of node.attributes) {
    open += ` ${attr.name}="${attr.value}"`;
  }

  const children = Array.from(node.childNodes).filter(c =>
    c.nodeType === Node.ELEMENT_NODE ||
    c.nodeType === Node.COMMENT_NODE ||
    c.nodeType === Node.CDATA_SECTION_NODE ||
    (c.nodeType === Node.TEXT_NODE && c.textContent.trim())
  );

  if (children.length === 0) {
    lines.push(indent + open + ' />');
    return;
  }

  if (children.length === 1 && children[0].nodeType === Node.TEXT_NODE) {
    const text = children[0].textContent.trim();
    lines.push(indent + open + '>' + text + '</' + node.tagName + '>');
    return;
  }

  lines.push(indent + open + '>');
  for (const child of children) {
    _serializeNode(child, depth + 1, lines);
  }
  lines.push(indent + '</' + node.tagName + '>');
}
